import React, { useState, useEffect } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from '@heroui/react';
import { Typography, CustomHeroButton, ResponsiveModal } from '../../../../components';
import CustomHeroInput from '../../../../components/CustomHeroInput';
import CustomTextarea from '../../../../components/CustomTextarea';
import { useTranslations } from '../../../../contexts/translation.context';
import { PartnerOrderData } from '../types';
import { Icon } from '@iconify/react';

interface EditCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: PartnerOrderData | null;
  onSave: (data: CampaignEditData) => void;
}

export interface CampaignEditData {
  name: string;
  notes?: string;
}

const EditCampaignModal: React.FC<EditCampaignModalProps> = ({
  isOpen,
  onClose,
  order,
  onSave,
}) => {
  const { t } = useTranslations();
  const [formData, setFormData] = useState<CampaignEditData>({
    name: '',
    notes: '',
  });
  const [errors, setErrors] = useState<{ name?: string }>({});
  const [touched, setTouched] = useState<{ name?: boolean }>({});
  const [teamsCount] = useState(0); // TODO: Implement teams functionality

  useEffect(() => {
    if (order) {
      // Generate campaign name from link or use order id
      const campaignId = order.campaign.link?.split('/').filter(Boolean).pop() || '';
      const campaignName = campaignId || order.id.slice(-4);

      setFormData({
        name: campaignName,
        notes: '',
      });
      setErrors({});
      setTouched({});
    }
  }, [order]);

  const validateName = (name: string): string | undefined => {
    if (!name.trim()) {
      return t('partner.orders.editCampaign.errors.nameRequired');
    }
    return undefined;
  };

  const handleNameChange = (value: string) => {
    setFormData((prev) => ({ ...prev, name: value }));
    if (touched.name) {
      setErrors((prev) => ({ ...prev, name: validateName(value) }));
    }
  };

  const handleNameBlur = () => {
    setTouched((prev) => ({ ...prev, name: true }));
    setErrors((prev) => ({ ...prev, name: validateName(formData.name) }));
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, notes: e.target.value }));
  };

  const handleSave = () => {
    const nameError = validateName(formData.name);

    if (nameError) {
      setErrors({ name: nameError });
      setTouched({ name: true });
      return;
    }

    onSave(formData);
    onClose();
  };

  const handleClose = () => {
    setFormData({ name: '', notes: '' });
    setErrors({});
    setTouched({});
    onClose();
  };

  const handleTeamsClick = () => {
    // TODO: Implement teams modal
    console.log('Open teams modal');
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      classNames={{
        base: 'md:max-w-[37.5rem]',
        backdrop: 'bg-black/50',
      }}
    >
      <ModalContent className="rounded-4">
        <ModalHeader className="flex flex-col gap-3 px-4 md:px-6 pt-6 pb-4">
          <Typography variant="h4" className="font-medium text-auth-ink text-xl">
            {t('partner.orders.editCampaign.title')}
          </Typography>
        </ModalHeader>

        <ModalBody className="px-4 md:px-6 py-3">
          <div className="flex flex-col gap-4">
            <CustomHeroInput
              label={t('partner.orders.editCampaign.campaignName')}
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              onBlur={handleNameBlur}
              placeholder="blue sky 06.05.2025"
              error={errors.name}
              touched={touched.name}
            />

            <CustomTextarea
              label={t('partner.orders.editCampaign.notes')}
              value={formData.notes}
              onChange={handleNotesChange}
              placeholder={t('partner.orders.editCampaign.notesPlaceholder')}
              minRows={6}
              maxRows={8}
            />

            <div className="flex flex-col gap-2">
              <Typography variant="body2" className="text-auth-ink">
                {t('partner.orders.editCampaign.addedTeams')}
              </Typography>
              <Button
                className="bg-[#E2EBF7] h-auto px-3.5 py-2.5 rounded-3 w-fit"
                onPress={handleTeamsClick}
              >
                <div className="flex items-center gap-1">
                  <Icon
                    icon="solar:settings-linear"
                    width={24}
                    height={24}
                    className="text-partner"
                  />
                  <Typography variant="body2" className="!text-partner font-semibold">
                    {t('partner.orders.editCampaign.teamsEnabled')}: {teamsCount}
                  </Typography>
                </div>
              </Button>
            </div>
          </div>
        </ModalBody>

        <ModalFooter className="px-6 pb-4 pt-0 h-[4.5rem] flex items-center justify-end">
          <div className="flex gap-2">
            <Button
              className="bg-[rgba(243,18,96,0.2)] h-10 px-4 rounded-3 min-w-[5.625rem]"
              onPress={handleClose}
            >
              <Typography variant="body2" className="!text-auth-strength-weak font-normal">
                {t('partner.orders.editCampaign.cancel')}
              </Typography>
            </Button>
            <Button
              className="bg-partner h-10 px-3.5 py-2.5 rounded-3 min-w-[6.25rem]"
              onPress={handleSave}
            >
              <Typography variant="body2" className="!text-white font-semibold">
                {t('partner.orders.editCampaign.save')}
              </Typography>
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
};

export default EditCampaignModal;
